import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { formatCurrencyAmount } from '../../shared/trade-formatters';
import { parseInvoiceLineAmount } from '../invoice-line-amount';
import { RelatedRecordsSelector } from '../related-records-selector/related-records-selector';
import {
  BondDocumentationOption,
  CreateInvoiceInput,
  FrequencyOption,
  InvoiceLineDraft,
  InvoiceLineInput,
  InvoiceLinkType,
  InvoiceLinkTypeCode,
  RelatedRecordLinkTypeCode,
  RelatedRecordSelection,
  InvoiceTypeOption,
  LinkTypeOption,
  Vendor,
} from '../invoices.types';
import { InvoicesService } from '../invoices.service';

interface InvoiceFormState {
  vendorId: number | null;
  linkType: InvoiceLinkType;
  linkedBondDocumentationId: number | null;
  issueDate: string;
  invoiceNumber: string;
  serviceDateStart: string;
  serviceDateEnd: string;
  currency: string;
  frequencyCode: string;
  recipientEmail: string;
  amountText: string | number;
}

@Component({
  selector: 'app-invoice-create',
  imports: [CommonModule, FormsModule, RouterLink, RelatedRecordsSelector],
  templateUrl: './invoice-create.html',
  styleUrls: ['../invoice-sheet.css', './invoice-create.css'],
})
export class InvoiceCreate implements OnInit {
  private readonly invoicesService = inject(InvoicesService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly loadedVendorConceptIds = new Set<number>();

  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly invoiceTypeOptions = signal<InvoiceTypeOption[]>([]);
  protected readonly frequencyOptions = signal<FrequencyOption[]>([]);
  protected readonly linkTypeOptions = signal<LinkTypeOption[]>([]);
  protected readonly bondDocumentationOptions = signal<BondDocumentationOption[]>([]);
  protected readonly lineDrafts = signal<InvoiceLineDraft[]>([]);
  protected readonly isLinkModalOpen = signal(false);
  protected readonly attachmentName = signal<string>('');
  protected readonly attachmentFile = signal<File | null>(null);
  protected readonly validationMessage = signal('');
  protected readonly isLoading = signal(false);
  protected readonly isSubmitting = signal(false);
  protected readonly submittingAction = signal<'email' | 'save' | 'save-send' | null>(null);
  protected readonly loadError = signal('');
  protected readonly form = signal<InvoiceFormState>({
    vendorId: null,
    linkType: 'Generic',
    linkedBondDocumentationId: null,
    issueDate: new Date().toISOString().slice(0, 10),
    invoiceNumber: '',
    serviceDateStart: '',
    serviceDateEnd: '',
    currency: 'USD',
    frequencyCode: '',
    recipientEmail: '',
    amountText: '',
  });

  protected readonly selectedVendor = computed(() => {
    const selectedVendorId = this.form().vendorId;
    return this.vendors().find((vendor) => vendor.id === selectedVendorId);
  });

  protected readonly availableInvoiceConceptOptions = computed(() => {
    const selected = this.selectedVendor();
    if (selected?.emittedInvoiceConceptIds.length) {
      const allowed = new Set(selected.emittedInvoiceConceptIds);
      return this.invoiceTypeOptions().filter((item) => allowed.has(item.id));
    }

    return this.invoiceTypeOptions();
  });

  protected readonly lineTotal = computed(() => {
    return this.lineDrafts().reduce((sum, line) => {
      const value = Number(line.amountText);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
  });

  protected readonly enteredInvoiceTotal = computed(() => {
    const rawAmount = this.toText(this.form().amountText).trim();
    if (!rawAmount) {
      return this.lineTotal();
    }

    const value = Number(rawAmount);
    return Number.isFinite(value) ? value : 0;
  });

  protected readonly linkSelectionSummary = computed(() => {
    const linked = this.lineDrafts().filter((line) => line.linkTypeCode !== 'NONE');
    const types = new Set(linked.map((line) => line.linkTypeCode));
    if (types.size > 1) {
      return `${linked.length} linked records · ${this.formatLinkTypes(types)}`;
    }
    return linked.length
      ? `${linked.length} linked records · ${this.formatLinkTypes(types)}`
      : 'No linked records selected';
  });

  protected readonly selectedLinkTypeCode = computed<RelatedRecordLinkTypeCode>(() => {
    return this.toLinkTypeCode(this.form().linkType) as RelatedRecordLinkTypeCode;
  });

  protected readonly modalInitialSelections = computed<RelatedRecordSelection[]>(() => {
    const mode = this.selectedLinkTypeCode();
    const seen = new Set<string>();
    return this.lineDrafts()
      .filter((line) => this.isLineInCurrentSelectionScope(line) && !!line.tradeId)
      .filter((line) => !seen.has(line.tradeId) && !!seen.add(line.tradeId))
      .map((line) => ({
        record: {
          linkTypeCode: mode,
          tradeId: line.tradeId,
          dealId: line.dealId,
          securityId: line.securityId,
          bondDocumentationId: line.bondDocumentationId,
          bondDocumentationName: line.bondDocumentationName,
          seriesNumber: line.seriesNumber,
          marketType: line.marketType,
          securityType: '',
          currency: line.tradeCcy,
          tradeDate: '',
          settlementDate: '',
          maturityDate: '',
          notional: 0,
          cusip: '',
          isin: '',
          tranche: '',
        },
        amountText: line.amountText,
      }));
  });

  ngOnInit(): void {
    this.isLoading.set(true);
    forkJoin({
      vendors: this.invoicesService.getVendorOptions(),
      conceptOptions: this.invoicesService.getInvoiceTypeOptions(),
      frequencyOptions: this.invoicesService.getFrequencyOptions(),
      linkTypeOptions: this.invoicesService.getLinkTypeOptions(),
      bondDocumentationOptions: this.invoicesService.getBondDocumentationOptions(),
    }).subscribe({
      next: ({ vendors, conceptOptions, frequencyOptions, linkTypeOptions, bondDocumentationOptions }) => {
        this.vendors.set(vendors);
        this.invoiceTypeOptions.set(conceptOptions);
        this.frequencyOptions.set(frequencyOptions);
        this.linkTypeOptions.set(linkTypeOptions);
        this.bondDocumentationOptions.set(bondDocumentationOptions);
        this.setDefaults();
        this.applyVendorFromQueryParam();
        const vendorId = this.form().vendorId;
        if (vendorId) {
          this.loadVendorConcepts(vendorId, () => this.isLoading.set(false));
        } else {
          this.isLoading.set(false);
        }
      },
      error: () => {
        this.loadError.set('Unable to load invoice creation data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onVendorChange(vendorIdRaw: string): void {
    const vendorId = Number(vendorIdRaw);
    const selected = this.vendors().find((vendor) => vendor.id === vendorId);
    const current = this.form();

    this.form.set({
      ...current,
      vendorId: Number.isFinite(vendorId) ? vendorId : null,
      currency: selected?.defaultCurrency ?? current.currency,
    });

    if (selected) {
      this.loadVendorConcepts(selected.id);
    }
  }

  protected onLinkTypeChange(linkType: InvoiceLinkType): void {
    const current = this.form();
    this.form.set({
      ...current,
      linkType,
      linkedBondDocumentationId: linkType === 'Program' ? current.linkedBondDocumentationId : null,
    });

    if (linkType === 'Program' && !this.form().linkedBondDocumentationId) {
      return;
    }

    this.openLinkModal();
  }

  protected onProgramChange(bondDocumentationId: number | null): void {
    this.onFieldChange('linkedBondDocumentationId', bondDocumentationId);
    if (bondDocumentationId) {
      this.openLinkModal();
    }
  }

  protected addGenericLine(): void {
    this.lineDrafts.set([...this.lineDrafts(), this.createLineDraft()]);
  }

  protected duplicateLine(key: string): void {
    const source = this.lineDrafts().find((line) => line.key === key);
    if (!source) {
      return;
    }

    const clone: InvoiceLineDraft = {
      ...source,
      key: `line-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      invConceptId: null,
      detail: '',
    };
    const index = this.lineDrafts().findIndex((line) => line.key === key);
    const next = [...this.lineDrafts()];
    next.splice(index + 1, 0, clone);
    this.lineDrafts.set(next);
  }

  protected removeLine(key: string): void {
    this.lineDrafts.set(this.lineDrafts().filter((line) => line.key !== key));
  }

  protected onLineFieldChange<K extends keyof InvoiceLineDraft>(key: string, field: K, value: InvoiceLineDraft[K]): void {
    this.lineDrafts.set(this.lineDrafts().map((line) => (line.key === key ? { ...line, [field]: value } : line)));
  }

  protected onFieldChange<K extends keyof InvoiceFormState>(key: K, value: InvoiceFormState[K]): void {
    const current = this.form();
    this.form.set({ ...current, [key]: value });
  }

  protected openLinkModal(): void {
    if (this.form().linkType === 'Generic') {
      this.validationMessage.set('Choose Deal ID, Security ID, or Program before adding linked records.');
      return;
    }
    if (this.form().linkType === 'Program' && !this.form().linkedBondDocumentationId) {
      this.validationMessage.set('Select a program before adding linked records.');
      return;
    }

    this.validationMessage.set('');
    this.isLinkModalOpen.set(true);
  }

  protected closeLinkModal(): void {
    this.isLinkModalOpen.set(false);
  }

  protected saveLinkModalSelection(selections: RelatedRecordSelection[]): void {
    const selectedTradeIds = new Set(selections.map(({ record }) => record.tradeId));
    const existing = this.lineDrafts();
    const defaultConceptId = this.availableInvoiceConceptOptions()[0]?.id ?? null;
    const byTradeId = new Map(selections.map((selection) => [selection.record.tradeId, selection]));
    const kept = existing
      .filter((line) => !this.isLineInCurrentSelectionScope(line) || selectedTradeIds.has(line.tradeId))
      .map((line) => {
        const selection = this.isLineInCurrentSelectionScope(line) ? byTradeId.get(line.tradeId) : undefined;
        return selection ? this.applySelectionToLine(line, selection) : line;
      });
    const added = selections
      .filter(({ record }) => !existing.some((line) => this.isLineInCurrentSelectionScope(line) && line.tradeId === record.tradeId))
      .map((selection) => ({
        ...this.createLineDraft(selection),
        invConceptId: defaultConceptId,
      }));

    this.lineDrafts.set([...kept, ...added]);
    this.closeLinkModal();
  }

  protected onAttachmentSelected(files: FileList | null): void {
    const firstFile = files?.item(0) ?? null;
    this.attachmentFile.set(firstFile);
    this.attachmentName.set(firstFile?.name ?? '');
  }

  protected clearAttachment(): void {
    this.attachmentFile.set(null);
    this.attachmentName.set('');
  }

  protected submit(): void {
    this.createInvoice(false);
  }

  protected submitAndSendEmail(): void {
    this.createInvoice(true);
  }

  protected sendEmailOnly(): void {
    if (this.isSubmitting()) {
      return;
    }

    const invoiceCode = this.form().invoiceNumber.trim();
    if (!invoiceCode) {
      this.validationMessage.set('Invoice number is required to send the approval email.');
      return;
    }

    const confirmed = window.confirm(`Send approval email for invoice "${invoiceCode}" now?`);
    if (!confirmed) {
      return;
    }

    this.isSubmitting.set(true);
    this.submittingAction.set('email');
    this.validationMessage.set('');
    this.invoicesService.sendInvoiceApprovalEmail({
      invoiceCode,
      recipientEmail: this.form().recipientEmail || null,
      messageDetail: this.buildApprovalMessage(invoiceCode),
    }).subscribe({
      next: (sent) => {
        this.isSubmitting.set(false);
        this.submittingAction.set(null);
        if (!sent) {
          this.validationMessage.set('Unable to send approval email. Save the invoice first or verify the invoice code.');
          return;
        }

        window.KTToast?.show({
          message: 'Approval email sent successfully.',
          variant: 'success',
        });
      },
      error: () => {
        this.isSubmitting.set(false);
        this.submittingAction.set(null);
        this.validationMessage.set('Unable to send approval email.');
      },
    });
  }

  private createInvoice(sendEmailAfterSave: boolean): void {
    if (this.isSubmitting()) {
      return;
    }

    if (!this.validateForm()) {
      return;
    }

    const payload = this.buildPayload();
    if (!payload) {
      return;
    }

    this.isSubmitting.set(true);
    this.submittingAction.set(sendEmailAfterSave ? 'save-send' : 'save');
    this.invoicesService.createInvoice(payload).subscribe({
      next: ({ result: invoiceId, documentError }) => {
        if (invoiceId <= 0) {
          this.isSubmitting.set(false);
          this.submittingAction.set(null);
          this.validationMessage.set('Unable to create invoice.');
          return;
        }

        if (documentError) {
          window.KTToast?.show({
            message: `Invoice created, but its document could not be uploaded. Upload it from the invoice detail. ${documentError}`,
            variant: 'warning',
          });
        }

        const invoiceCode = payload.invoiceNumber;
        if (!sendEmailAfterSave) {
          this.isSubmitting.set(false);
          this.submittingAction.set(null);
          if (!documentError) {
            window.KTToast?.show({
              message: 'Invoice added successfully.',
              variant: 'success',
            });
          }
          void this.router.navigate(['/invoices']);
          return;
        }

        this.invoicesService.sendInvoiceApprovalEmail({
          invoiceCode,
          recipientEmail: this.form().recipientEmail || null,
          messageDetail: this.buildApprovalMessage(invoiceCode),
        }).subscribe({
          next: (sent) => {
            this.isSubmitting.set(false);
            this.submittingAction.set(null);
            if (!sent) {
              this.validationMessage.set('Invoice was created, but approval email could not be sent.');
              return;
            }

            window.KTToast?.show({
              message: 'Invoice added and approval email sent.',
              variant: 'success',
            });
            void this.router.navigate(['/invoices']);
          },
          error: () => {
            this.isSubmitting.set(false);
            this.submittingAction.set(null);
            this.validationMessage.set('Invoice was created, but approval email could not be sent.');
          },
        });
      },
      error: () => {
        this.isSubmitting.set(false);
        this.submittingAction.set(null);
        this.validationMessage.set('Unable to create invoice.');
      },
    });
  }

  protected formatAmount(currency: string, amount: number): string {
    return formatCurrencyAmount(currency, amount);
  }

  protected getCommittedAmountForTrade(tradeId: string): number {
    return this.lineDrafts()
      .filter((line) => line.tradeId === tradeId)
      .reduce((sum, line) => {
        const value = Number(line.amountText);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
  }

  protected formatLinkDisplay(): string {
    if (this.form().linkType === 'Program' && this.form().linkedBondDocumentationId) {
      const selected = this.bondDocumentationOptions().find((item) => item.id === this.form().linkedBondDocumentationId);
      return selected ? `${this.form().linkType} (${selected.name})` : this.form().linkType;
    }

    return this.form().linkType;
  }

  private validateForm(): boolean {
    const current = this.form();
    this.validationMessage.set('');

    if (!current.vendorId || !current.invoiceNumber.trim() || !current.issueDate) {
      this.validationMessage.set('Vendor, invoice number, and issue date are required.');
      return false;
    }

    if (!this.attachmentFile()) {
      this.validationMessage.set('Attach the invoice document before saving.');
      return false;
    }

    if (this.toText(current.amountText).trim()) {
      const amount = Number(current.amountText);
      if (!Number.isFinite(amount) || amount < 0) {
        this.validationMessage.set('Invoice total amount must be a valid non-negative number.');
        return false;
      }
    }

    return this.validateLines();
  }

  /**
   * Mirrors the backend line rules: every line needs a concept and a valid amount, trade-linked
   * modes need a trade, and the same concept cannot repeat over the same trade reference.
   */
  private validateLines(): boolean {
    const lines = this.lineDrafts();
    if (lines.length === 0) {
      this.validationMessage.set('Add at least one invoice line.');
      return false;
    }

    const seen = new Set<string>();

    for (const line of lines) {
      if (!line.invConceptId) {
        this.validationMessage.set('Every invoice line needs a concept.');
        return false;
      }

      if (parseInvoiceLineAmount(line.amountText) === null) {
        this.validationMessage.set('Every invoice line needs a valid non-negative amount.');
        return false;
      }

      if (line.linkTypeCode !== 'NONE' && !line.tradeId) {
        this.validationMessage.set('Every invoice line needs a linked trade for this reference type.');
        return false;
      }
      if (line.linkTypeCode === 'DEAL' && !line.dealId) {
        this.validationMessage.set('Every Deal ID line needs a deal reference.');
        return false;
      }
      if (line.linkTypeCode === 'SECURITY' && !line.securityId) {
        this.validationMessage.set('Every Security ID line needs a security reference.');
        return false;
      }
      if (line.linkTypeCode === 'BOND_DOCUMENTATION' && !line.bondDocumentationId) {
        this.validationMessage.set('Every Program line needs a program reference.');
        return false;
      }

      const signature = `${line.invConceptId}|${line.linkTypeCode}|${line.tradeId}|${line.dealId}|${line.securityId}|${line.bondDocumentationId ?? ''}`;
      if (seen.has(signature)) {
        this.validationMessage.set('The same concept cannot repeat on the same linked record.');
        return false;
      }
      seen.add(signature);
    }

    return true;
  }

  private buildPayload(): CreateInvoiceInput | null {
    const current = this.form();
    const amount = this.enteredInvoiceTotal();

    if (!Number.isFinite(amount) || amount < 0) {
      this.validationMessage.set('Invoice total amount must be a valid non-negative number.');
      return null;
    }

    return {
      vendorId: current.vendorId ?? 0,
      issueDate: current.issueDate,
      linkType: this.headerLinkType(),
      invoiceNumber: current.invoiceNumber.trim(),
      serviceDateStart: current.serviceDateStart || null,
      serviceDateEnd: current.serviceDateEnd || null,
      currency: current.currency.trim().toUpperCase(),
      frequencyCode: current.frequencyCode || null,
      amount,
      attachmentName: this.attachmentName() || null,
      attachmentFile: this.attachmentFile(),
      lines: this.buildLinePayload(),
    };
  }

  private buildLinePayload(): InvoiceLineInput[] {
    return this.lineDrafts().map((line) => {
      return {
        invConceptId: line.invConceptId ?? 0,
        linkTypeCode: line.linkTypeCode,
        tradeId: line.linkTypeCode === 'NONE' ? null : line.tradeId,
        dealId: line.linkTypeCode === 'DEAL' ? line.dealId : null,
        securityId: line.linkTypeCode === 'SECURITY' ? line.securityId : null,
        bondDocumentationId: line.linkTypeCode === 'BOND_DOCUMENTATION' ? line.bondDocumentationId : null,
        tradeCcy: line.linkTypeCode === 'NONE' ? null : line.tradeCcy,
        lineAmount: Number(line.amountText) || 0,
        detail: line.detail.trim() || null,
      };
    });
  }

  private toLinkTypeCode(linkType: InvoiceLinkType): InvoiceLinkTypeCode {
    switch (linkType) {
      case 'Deal ID':
        return 'DEAL';
      case 'Security ID':
        return 'SECURITY';
      case 'Program':
        return 'BOND_DOCUMENTATION';
      default:
        return 'NONE';
    }
  }

  private createLineDraft(selection?: RelatedRecordSelection): InvoiceLineDraft {
    const record = selection?.record;
    return {
      key: `line-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      invConceptId: this.availableInvoiceConceptOptions()[0]?.id ?? null,
      linkTypeCode: record?.linkTypeCode ?? 'NONE',
      tradeId: record?.tradeId ?? '',
      dealId: record?.dealId ?? '',
      securityId: record?.securityId ?? '',
      bondDocumentationId: record?.bondDocumentationId ?? null,
      bondDocumentationName: record?.bondDocumentationName ?? '',
      tradeCcy: record?.currency ?? '',
      seriesNumber: record?.seriesNumber ?? '',
      marketType: record?.marketType ?? '',
      amountText: selection?.amountText ?? '',
      detail: '',
    };
  }

  private applySelectionToLine(line: InvoiceLineDraft, selection: RelatedRecordSelection): InvoiceLineDraft {
    const record = selection.record;
    const matchingLineCount = this.lineDrafts().filter((candidate) => (
      this.isLineInCurrentSelectionScope(candidate) && candidate.tradeId === record.tradeId
    )).length;
    return {
      ...line,
      tradeId: record.tradeId,
      dealId: record.dealId,
      securityId: record.securityId,
      bondDocumentationId: record.bondDocumentationId,
      bondDocumentationName: record.bondDocumentationName,
      tradeCcy: record.currency,
      seriesNumber: record.seriesNumber,
      marketType: record.marketType,
      amountText: matchingLineCount === 1 ? selection.amountText : line.amountText,
    };
  }

  private isLineInCurrentSelectionScope(line: InvoiceLineDraft): boolean {
    const mode = this.selectedLinkTypeCode();
    return line.linkTypeCode === mode
      && (mode !== 'BOND_DOCUMENTATION' || line.bondDocumentationId === this.form().linkedBondDocumentationId);
  }

  private headerLinkType(): InvoiceLinkType {
    const linkedTypes = new Set(this.lineDrafts().map((line) => line.linkTypeCode));
    return linkedTypes.size === 1 ? this.toUiLinkType([...linkedTypes][0]) : 'Generic';
  }

  private toUiLinkType(linkTypeCode: InvoiceLinkTypeCode): InvoiceLinkType {
    switch (linkTypeCode) {
      case 'DEAL':
        return 'Deal ID';
      case 'SECURITY':
        return 'Security ID';
      case 'BOND_DOCUMENTATION':
        return 'Program';
      default:
        return 'Generic';
    }
  }

  private setDefaults(): void {
    const vendor = this.vendors()[0];

    this.form.set({
      ...this.form(),
      vendorId: vendor?.id ?? null,
      currency: vendor?.defaultCurrency ?? 'USD',
      frequencyCode: '',
      recipientEmail: '',
    });
    this.lineDrafts.set([]);
  }

  private applyVendorFromQueryParam(): void {
    const vendorIdRaw = this.route.snapshot.queryParamMap.get('vendorId');
    const vendorId = Number(vendorIdRaw);
    if (!Number.isFinite(vendorId) || vendorId <= 0) {
      return;
    }

    const selectedVendor = this.vendors().find((vendor) => vendor.id === vendorId);
    if (!selectedVendor) {
      return;
    }

    this.form.set({
      ...this.form(),
      vendorId: selectedVendor.id,
      currency: selectedVendor.defaultCurrency,
    });
  }

  private loadVendorConcepts(vendorId: number, complete?: () => void): void {
    if (this.loadedVendorConceptIds.has(vendorId)) {
      complete?.();
      return;
    }

    this.loadedVendorConceptIds.add(vendorId);
    this.invoicesService.getVendorInvoiceConceptIds(vendorId).subscribe({
      next: (conceptIds) => {
        this.vendors.update((vendors) => vendors.map((vendor) => vendor.id === vendorId
          ? { ...vendor, emittedInvoiceConceptIds: conceptIds }
          : vendor));
        complete?.();
      },
      error: () => {
        this.loadedVendorConceptIds.delete(vendorId);
        complete?.();
      },
    });
  }

  private formatLinkTypes(types: Set<InvoiceLinkTypeCode>): string {
    const order: InvoiceLinkTypeCode[] = ['DEAL', 'SECURITY', 'BOND_DOCUMENTATION'];
    return order.filter((type) => types.has(type)).map((type) => this.toUiLinkType(type)).join(' + ');
  }

  private toText(value: string | number): string {
    return typeof value === 'number' ? `${value}` : value;
  }

  private buildApprovalMessage(invoiceCode: string): string {
    return `This invoice is approved. Invoice code: ${invoiceCode}.`;
  }
}
